import { NFTI } from '@tacans/shared-marketplace-layer';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withUniverseVerification from 'src/HOCs/withUniverseVerification';
import PageMeta from 'src/components/organisms/page-meta';
import { PAGES_LOCALES } from 'src/constants/common.constants';
import { CollectionDetailsContainer } from 'src/features/common/collection/containers/collection-details-container';
import { collectionFetchThunk } from 'src/features/common/collection/ducks';
import { useCollectionById } from 'src/features/common/collection/hooks/collection.hooks';
import { ExploreContentLayout } from 'src/features/common/explore/components/explore-content-layout';
import { FilterButton } from 'src/features/common/explore/components/filter-button';
import { ExploreFilterContainer } from 'src/features/common/explore/containers/explore-filter-container';
import { ExploreTopFiltersContainer } from 'src/features/common/explore/containers/explore-top-filters-container';
import { NftsExploreContainer } from 'src/features/common/explore/containers/nfts-explore-container';
import { INITIAL_FILTER_STATE } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.constants';
import { actions } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.slice';
import { fetchNftsThunk } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.thunk';
import {
    useClearExplore,
    useFilters,
    useIsFiltersApplied,
    useSetFilters,
    useTotal,
} from 'src/features/common/explore/hooks/use-explore-nfts';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { fetchUsersByWalletsThunk, fetchUsersByIdsThunk } from 'src/features/common/user/ducks/user-list';
import { CollectionPage } from 'src/features/white-labels/ocean/collection/collection-page';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useMeta } from 'src/hooks/use-meta';
import { wrapper } from 'src/store';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { Visible } from 'src/ui/common/layouts/visible';
import { SimpleTabs } from 'src/ui/common/molecules/simple-tabs';

const CollectionDetailsPage = () => {
    const total = useTotal();
    const { keywords } = useFilters();
    const isFiltersApplied = useIsFiltersApplied('collection');
    const clearExplore = useClearExplore();
    const setFilters = useSetFilters();
    const { projectName, domain } = useUniverse();
    const [sideBarOpened, setSideBarOpened] = useState(true);
    const { t } = useTranslation(['components']);
    const toggleSideBar = useCallback(() => setSideBarOpened((s) => !s), []);
    const { isOceanTemplate } = useGetUniverseTemplate();

    const router = useRouter();
    const { url: id } = router.query;

    const collection = useCollectionById(id as string);

    const meta = useMeta('collection', {
        projectName,
        collectionName: collection?.name!,
        projectUrl: domain!,
    });

    const tabs = useMemo(() => [{ tabId: 'items', tab: total ? `Items (${total})` : 'Items' }], [total]);

    const clearFilters = useCallback(() => {
        setFilters({
            ...INITIAL_FILTER_STATE.filter,
            collectionId: [id as string],
        });
    }, [id, setFilters]);

    const emptyList = useMemo(
        () => ({
            title: t('emptyList.nft.title', { ns: 'components' }),
            description: t(`emptyList.nft.${isFiltersApplied || !!keywords ? 'textSearch' : 'text'}`, {
                ns: 'components',
            }),
        }),
        [t, isFiltersApplied, keywords]
    );

    const emptyPlaceholder = useCallback(
        ({ itemsGridConfig }) => (
            <EmptyListPlaceholder itemsGridConfig={itemsGridConfig}>
                <Typography size="base" tag="div" variant="title" color="text">
                    {emptyList.title}
                </Typography>
                <Spacing margin="10px 0 30px">
                    <Typography size="sm" tag="div" color="gs200">
                        <span dangerouslySetInnerHTML={{ __html: emptyList.description }} />
                    </Typography>
                </Spacing>
            </EmptyListPlaceholder>
        ),
        [emptyList]
    );

    useEffect(() => {
        clearFilters();
        return clearExplore;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isOceanTemplate) {
        return (
            <CollectionPage
                title={meta.title}
                description={meta.description}
                originalUrl={collection?.previewAssets?.originalUrl}
                id={id as string}
                isFiltersApplied={isFiltersApplied}
                clearFilters={clearFilters}
                sideBarOpened={sideBarOpened}
                toggleSideBar={toggleSideBar}
                tabs={tabs}
                emptyPlaceholder={emptyPlaceholder}
            />
        );
    }

    return (
        <>
            <PageMeta
                title={meta.title}
                description={meta.description}
                ogImage={collection?.previewAssets?.originalUrl}
            />
            <PageLayout fullWidth>
                <CollectionDetailsContainer collectionId={id as string} />
                <SimpleTabs initialTabId="items">
                    <ExploreContentLayout>
                        <ExploreContentLayout.TopPanel>
                            <Visible view={['desktop', 'laptop']}>
                                <FilterButton
                                    showClearBtn={isFiltersApplied}
                                    onClear={clearFilters}
                                    opened={sideBarOpened}
                                    onToggle={toggleSideBar}
                                />
                            </Visible>
                            <SimpleTabs.TabItems tabItems={tabs} />
                        </ExploreContentLayout.TopPanel>
                        <ExploreContentLayout.Content>
                            <ExploreContentLayout.SideBar opened={sideBarOpened}>
                                <ExploreFilterContainer />
                            </ExploreContentLayout.SideBar>
                            <ExploreContentLayout.ExploreContent>
                                <SimpleTabs.Content tabId="items">
                                    <ExploreTopFiltersContainer />
                                    <NftsExploreContainer
                                        sideBarOpened={sideBarOpened}
                                        emptyPlaceholder={emptyPlaceholder}
                                    />
                                </SimpleTabs.Content>
                            </ExploreContentLayout.ExploreContent>
                        </ExploreContentLayout.Content>
                    </ExploreContentLayout>
                </SimpleTabs>
            </PageLayout>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
    const { locale, params } = ctx;
    const { url: id } = params || {};
    const props = {
        ...(await serverSideTranslations(locale as string, PAGES_LOCALES)),
    };

    if (!id || isNaN(+id)) {
        return { props, notFound: true };
    }

    try {
        // @ts-ignore
        store.dispatch(actions.updateFilter({ collectionId: [id as string] }));
        const res = await Promise.all([
            // @ts-ignore
            store.dispatch(fetchNftsThunk()),
            // @ts-ignore
            store.dispatch(collectionFetchThunk(id as string)),
        ]);

        const nftsData = res[0].payload;
        const data = res[1].payload;

        if (!data?.collection) {
            throw new Error(`Can't fetch collection with ID: ${id}`);
        }

        // @ts-ignore
        await store.dispatch(fetchUsersByIdsThunk([data.collection.ownerId]));
        if (nftsData?.list?.length) {
            const usersToFetch = nftsData.list.map((item: NFTI) => ({
                address: item.author,
                blockchainId: item.blockchainId,
            }));
            // @ts-ignore
            await store.dispatch(fetchUsersByWalletsThunk(usersToFetch));
        }

        return {
            props,
        };
    } catch (err) {
        return {
            props,
            notFound: true,
        };
    }
});

export default withUniverseVerification(CollectionDetailsPage);
